"use client";

import React, { useState } from "react";
import { Calendar, Clock, CircleCheck as CheckCircle2, DollarSign, ArrowRight, ShieldCheck, FileText, Hammer, Layers, Sparkles, Info, TrendingUp, Building, House as Home, Check } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
interface RenovationCategory {
  id: string;
  title: string;
  tagline: string;
  standardRange: string;
  highEndRange: string;
  avgTimeline: string;
  popularIn: string;
  scopeItem1: string;
  scopeItem2: string;
  scopeItem3: string;
  scopeItem4: string;
  driverItem1: string;
  driverItem2: string;
}
const renovationCategories: RenovationCategory[] = [{
  id: "kitchen",
  title: "Kitchen Remodeling",
  tagline: "Custom layouts, cabinetry, and chef-grade islands",
  standardRange: "$45,000 – $75,000",
  highEndRange: "$80,000 – $140,000+",
  avgTimeline: "6 – 10 weeks",
  popularIn: "Oakhurst & Winnona Park",
  scopeItem1: "Custom inset or frameless hardwood cabinetry",
  scopeItem2: "Quartzite, marble, or engineered quartz surfaces",
  scopeItem3: "Plumbing reconfiguration and gas line relocations",
  scopeItem4: "Architectural task, under-cabinet, and ambient lighting",
  driverItem1: "Appliance package selection & structural wall removal",
  driverItem2: "Cabinetry custom fabrication details & finish tiers"
}, {
  id: "bathroom",
  title: "Master & Guest Bathrooms",
  tagline: "Spa-grade wet rooms, dual vanities, and curbless showers",
  standardRange: "$24,000 – $42,000",
  highEndRange: "$48,000 – $85,000+",
  avgTimeline: "4 – 7 weeks",
  popularIn: "Great Lakes & Downtown Decatur",
  scopeItem1: "Curbless walk-in showers with linear drain systems",
  scopeItem2: "Custom stone vanities and integrated double sinks",
  scopeItem3: "Heated floor systems and precision porcelain tilework",
  scopeItem4: "Concealed plumbing fixtures and moisture ventilation",
  driverItem1: "Tile material complexity and custom glass enclosures",
  driverItem2: "Subfloor structural reinforcements and plumbing re-routes"
}, {
  id: "whole-home",
  title: "Whole-Home Renovations",
  tagline: "Comprehensive interior and exterior home modernization",
  standardRange: "$175,000 – $280,000",
  highEndRange: "$300,000 – $550,000+",
  avgTimeline: "16 – 26 weeks",
  popularIn: "Decatur Historic Districts",
  scopeItem1: "Complete space replanning and load-bearing alterations",
  scopeItem2: "New high-efficiency HVAC, updated wiring, and PEX plumbing",
  scopeItem3: "Site-finished hardwood flooring and architectural trim",
  scopeItem4: "Coordinated kitchen, multiple bathrooms, and living zones",
  driverItem1: "Total square footage, age of existing mechanicals",
  driverItem2: "Historic district preservation guidelines and review"
}, {
  id: "addition",
  title: "Home Additions",
  tagline: "Second-story expansions, primary suites, and bump-outs",
  standardRange: "$120,000 – $210,000",
  highEndRange: "$225,000 – $380,000+",
  avgTimeline: "14 – 20 weeks",
  popularIn: "Winnona Park & Oakhurst",
  scopeItem1: "Engineered foundation, framing, and roofing integration",
  scopeItem2: "Exterior cladding matched to existing Decatur architecture",
  scopeItem3: "Dedicated zoned climate control and electrical tie-ins",
  scopeItem4: "City of Decatur zoning setback and tree protection compliance",
  driverItem1: "Foundation requirements and tree canopy preservation",
  driverItem2: "Second-story structural tie-ins versus ground footprint"
}, {
  id: "basement",
  title: "Basement Remodeling",
  tagline: "Guest suites, media lounges, wet bars, and home gyms",
  standardRange: "$50,000 – $85,000",
  highEndRange: "$90,000 – $160,000+",
  avgTimeline: "8 – 12 weeks",
  popularIn: "Great Lakes & Decatur Heights",
  scopeItem1: "Subfloor waterproofing and vapor barrier installation",
  scopeItem2: "Egress window additions and building code compliance",
  scopeItem3: "Custom wet bar joinery, beverage stations, and baths",
  scopeItem4: "Acoustic ceiling treatments and recessed dimmable lighting",
  driverItem1: "Ceiling height clearances and concrete slab leveling",
  driverItem2: "Plumbing rough-in ejector pumps and egress additions"
}, {
  id: "outdoor",
  title: "Covered Outdoor Living & Decks",
  tagline: "Screened porches, outdoor fireplaces, and composite decking",
  standardRange: "$32,000 – $58,000",
  highEndRange: "$65,000 – $115,000+",
  avgTimeline: "4 – 8 weeks",
  popularIn: "City of Decatur Neighborhoods",
  scopeItem1: "Timber framing, vaulted beadboard ceilings, and roofing",
  scopeItem2: "Integrated motorized screens or custom timber screening",
  scopeItem3: "Composite decking, stone outdoor hearths, and heaters",
  scopeItem4: "Low-voltage accent lighting and outdoor audio wiring",
  driverItem1: "Roofline tie-in complexity and site elevation slopes",
  driverItem2: "Material choices: Ipe hardwood vs composite decking"
}];
const planningFactors = [{
  title: "City of Decatur vs. DeKalb County Permitting",
  subtitle: "Municipal Jurisdictions & Review Timelines",
  description: "Projects located inside the incorporated City of Decatur municipal limits undergo a distinct review process compared to unincorporated DeKalb County. City of Decatur reviews require strict tree canopy conservation calculations, impervious lot-coverage limits (typically capped at 40-50%), and historic district design reviews where applicable. Decatur plan reviews typically run 4 to 8 weeks. In contrast, unincorporated DeKalb County permitting focuses primarily on standard county zoning setbacks, environmental health, and standard building code queues, often requiring 3 to 6 weeks. Our pre-construction team prepares stamped architectural plans tailored to your exact jurisdiction from day one to avoid re-submittals.",
  icon: "shield"
}, {
  title: "Older Decatur Craftsman & Brick Ranch Considerations",
  subtitle: "Structural & Mechanical Sequencing for Vintage Homes",
  description: "Decatur's 1920s craftsman bungalows and 1950s mid-century ranches require specialized planning prior to demolition. Common conditions include ungrounded or knob-and-tube electrical circuits, aging cast-iron sewer lines, uninsulated crawlspaces, and undersized floor joists that have settled over decades. When planning kitchen expansions or second-story additions, we perform non-invasive laser level tests and structural probe inspections during pre-construction. This identifies subfloor leveling, framing sistering, and electrical panel upgrades early, building necessary contingency allowances into fixed contracts before hammers swing.",
  icon: "file"
}, {
  title: "Process Adaptation Across Project Types",
  subtitle: "Tailored Workflows for Kitchens, Additions & Basements",
  description: "Each renovation type follows a distinct sequence. Kitchen and bathroom remodels prioritize rapid MEP (mechanical, electrical, plumbing) rough-ins and dust-containment barriers so homeowners can maintain household rhythm. Home additions and whole-house renovations begin with civil site surveys, structural foundation engineering, and roof tie-in weatherproofing before interior work starts. Basement transformations require moisture mitigation, vapor barriers, and egress excavation before partition framing, while outdoor living builds focus on ground disturbance and post-footing structural load paths.",
  icon: "hammer"
}, {
  title: "Key Timeline Drivers & Acceleration Factors",
  subtitle: "Pre-Construction Decisions That Shorten Construction Duration",
  description: "A project's build timeline is heavily shaped by decisions finalized during pre-construction. Ordering custom cabinetry (typically an 8- to 12-week lead time), specialty European tile, custom architectural windows, and chef-grade appliances before demolition prevents on-site work stoppages. Scope changes made mid-project represent the most frequent cause of delays. By locking in all material specifications, finish schedules, and stamped engineering drawings during Phase 2 design development, we consistently reduce on-site construction schedules by 20% to 30%.",
  icon: "dollar"
}];
const costVsValueData = [{
  project: "Minor kitchen remodel (midrange)",
  cost: "$28,359",
  resaleValue: "$31,712",
  recouped: "111.8%"
}, {
  project: "Bath remodel (midrange)",
  cost: "$25,304",
  resaleValue: "$19,595",
  recouped: "77.4%"
}, {
  project: "Basement remodel",
  cost: "$49,681",
  resaleValue: "$45,636",
  recouped: "91.9%"
}, {
  project: "Deck addition (composite)",
  cost: "$24,984",
  resaleValue: "$20,264",
  recouped: "81.1%"
}];
export default function CostGuide() {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const prefersReducedMotion = useReducedMotion();
  const filteredCategories = activeCategory === "all" ? renovationCategories : renovationCategories.filter(item => item.id === activeCategory);
  const containerVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="cost-guide" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-semibold tracking-wider uppercase mb-4 border border-border">
            <Sparkles className="size-3.5 text-primary" />
            <span>Decatur Investment Framework</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground text-balance">
            Realistic renovation investment ranges in Decatur.
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every Decatur residence carries unique architectural character, from 1920s craftsman bungalows in Oakhurst to mid-century brick ranches in Great Lakes and contemporary homes in Downtown Decatur. Planning an enduring renovation requires a clear understanding of realistic construction budgets, permit timelines, and engineering requirements before work begins.
          </p>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
            Below are typical investment brackets derived from completed local projects across the City of Decatur and surrounding DeKalb County neighborhoods, reflecting prevailing material costs, skilled trades, and municipal building codes.
          </p>
        </div>

        {/* Category Quick Filter Chips */}
        <div className="flex flex-wrap items-center gap-2 mb-10 pb-2">
          <Button variant={activeCategory === "all" ? "default" : "outline"} size="sm" onClick={() => setActiveCategory("all")} className="rounded-lg text-xs font-medium cursor-pointer transition-[color,background-color,border-color,box-shadow]">
            All Categories
          </Button>
          {renovationCategories.map((cat, i) => <div key={cat.id} data-index={i}>
              <Button variant={activeCategory === cat.id ? "default" : "outline"} size="sm" onClick={() => setActiveCategory(cat.id)} className="rounded-lg text-xs font-medium cursor-pointer transition-[color,background-color,border-color,box-shadow]">
                {cat.title}
              </Button>
            </div>)}
        </div>

        {/* Renovation Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {filteredCategories.map((category, i) => <motion.div key={category.id} data-index={i} variants={itemVariants} whileHover={prefersReducedMotion ? {} : {
          y: -4
        }} className="flex flex-col h-full rounded-xl bg-card border border-border p-6 sm:p-7 shadow-xs hover:shadow-md hover:border-primary/40 transition-[box-shadow,border-color] duration-300">
              {/* Card Header */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-serif text-xl sm:text-2xl font-semibold text-card-foreground">
                    {category.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {category.tagline}
                  </p>
                </div>
                <Badge variant="secondary" className="text-[11px] font-medium shrink-0">
                  {category.popularIn}
                </Badge>
              </div>

              {/* Pricing Highlight Box */}
              <div className="my-4 p-4 rounded-lg bg-secondary/60 border border-border/80">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Standard Scope
                  </span>
                  <span className="font-sans font-bold text-foreground text-sm sm:text-base">
                    {category.standardRange}
                  </span>
                </div>
                <Separator className="my-2.5 bg-border/70" />
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                    Custom / High-End
                  </span>
                  <span className="font-sans font-bold text-foreground text-sm sm:text-base">
                    {category.highEndRange}
                  </span>
                </div>
              </div>

              {/* Timeline Indicator */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-5 px-1">
                <Clock className="size-3.5 text-primary shrink-0" />
                <span>Typical duration:</span>
                <span className="font-semibold text-foreground">{category.avgTimeline}</span>
              </div>

              <Separator className="mb-5 bg-border/60" />

              {/* Key Scope Inclusions */}
              <div className="flex-1">
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
                  <Layers className="size-3.5 text-primary" />
                  <span>Standard Inclusions</span>
                </h4>
                <ul className="space-y-2 text-xs sm:text-sm text-card-foreground mb-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{category.scopeItem1}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{category.scopeItem2}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{category.scopeItem3}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{category.scopeItem4}</span>
                  </li>
                </ul>

                {/* Primary Cost Drivers */}
                <div className="bg-muted/40 rounded-lg p-3.5 border border-border/60 text-xs">
                  <span className="font-semibold text-foreground block mb-1">
                    Key Cost Drivers:
                  </span>
                  <p className="text-muted-foreground leading-relaxed">
                    1. {category.driverItem1}
                  </p>
                  <p className="text-muted-foreground leading-relaxed mt-1">
                    2. {category.driverItem2}
                  </p>
                </div>
              </div>

              {/* Card Footer Consultation Action */}
              <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
                <Link to="/ContactPage" className="text-xs sm:text-sm font-semibold text-primary hover:text-primary/80 inline-flex items-center gap-1.5 transition-colors group">
                  <span>Request detailed estimate</span>
                  <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
                <span className="text-[11px] text-muted-foreground font-mono">
                  Decatur, GA
                </span>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Atlanta Remodeling Cost vs Value Table */}
        <div className="mt-16 sm:mt-20">
          <div className="rounded-2xl bg-card border border-border p-6 sm:p-8 lg:p-10 shadow-xs backdrop-blur-md">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider border border-border mb-2">
                  <TrendingUp className="size-3.5 text-primary" />
                  <span>Market Intelligence</span>
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  Atlanta remodeling cost vs value (2025)
                </h3>
              </div>
              <Badge variant="outline" className="self-start sm:self-auto text-xs py-1 px-3 border-border">
                Atlanta Metro Benchmarks
              </Badge>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[560px]">
                <caption className="text-xs text-muted-foreground text-left py-2 font-medium">
                  National survey averages for the Atlanta market, not quotes
                </caption>
                <thead>
                  <tr className="border-b border-border bg-secondary/40 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    <th className="py-3.5 px-4 rounded-l-md">Project</th>
                    <th className="py-3.5 px-4 text-right">Average Cost</th>
                    <th className="py-3.5 px-4 text-right">Resale Value Added</th>
                    <th className="py-3.5 px-4 text-right rounded-r-md">% Cost Recouped</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60 text-sm">
                  {costVsValueData.map((row, idx) => <tr key={row.project} data-index={idx} className="hover:bg-secondary/30 transition-colors">
                      <td className="py-4 px-4 font-medium text-foreground">
                        {row.project}
                      </td>
                      <td className="py-4 px-4 text-right font-mono text-muted-foreground">
                        {row.cost}
                      </td>
                      <td className="py-4 px-4 text-right font-mono text-foreground font-semibold">
                        {row.resaleValue}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-primary/10 text-primary font-mono font-bold text-xs">
                          {row.recouped}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            <div className="mt-4 pt-4 border-t border-border/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-muted-foreground">
              <p>
                Source: Remodeling Magazine / JLC Cost vs Value Report 2025, Atlanta, GA metro.
              </p>
              <p className="italic">
                Actual project returns vary by neighborhood micro-location and finish specifications.
              </p>
            </div>
          </div>
        </div>

        {/* Detailed Permitting, Planning & Decatur Homes Framework */}
        <div className="mt-16 rounded-2xl bg-secondary/40 border border-border p-6 sm:p-8 lg:p-10">
          <div className="max-w-3xl mb-8">
            <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-md bg-background text-foreground text-xs font-semibold uppercase tracking-wider border border-border mb-3">
              <Info className="size-3.5 text-primary" />
              <span>Decatur Construction Realities</span>
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-foreground tracking-tight">
              Permits, vintage homes, and timeline variables in Decatur.
            </h3>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
              Navigating residential renovations in Decatur demands localized knowledge. From municipal tree canopy regulations to historical framing conventions, deliberate pre-construction planning prevents surprise delays and unbudgeted scope changes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {planningFactors.map((factor, i) => <div key={factor.title} data-index={i} className="bg-card p-6 rounded-xl border border-border shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="size-9 rounded-lg bg-secondary flex items-center justify-center text-primary shrink-0 border border-border/70">
                      {factor.icon === "shield" && <ShieldCheck className="size-4.5" />}
                      {factor.icon === "file" && <FileText className="size-4.5" />}
                      {factor.icon === "hammer" && <Hammer className="size-4.5" />}
                      {factor.icon === "dollar" && <DollarSign className="size-4.5" />}
                    </div>
                    <div>
                      <h4 className="font-serif font-semibold text-base sm:text-lg text-foreground leading-snug">
                        {factor.title}
                      </h4>
                      <p className="text-[11px] uppercase tracking-wider font-semibold text-primary">
                        {factor.subtitle}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mt-2">
                    {factor.description}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Planning Consultation Banner */}
          <div className="mt-8 pt-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="max-w-xl">
              <h5 className="font-sans font-bold text-foreground text-sm sm:text-base">
                Ready to review your home&apos;s feasibility and timeline?
              </h5>
              <p className="text-xs text-muted-foreground mt-1">
                We provide on-site structural consultations, preliminary zoning verification, and itemized scope estimates for Decatur homeowners.
              </p>
            </div>
            <Button asChild size="lg" className="rounded-lg font-semibold cursor-pointer shadow-sm shrink-0">
              <Link to="/ContactPage">
                Schedule Planning Consultation
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Cost Disclaimer Note */}
        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {"* Investment ranges reflect typical residential project scopes and prevailing 2025 material and skilled labor standards across Decatur and inside the I-285 perimeter. Exact estimates are finalized after comprehensive on-site architectural evaluation, structural review, and finish specification."}
          </p>
        </div>
      </div>
    </section>;
}